import { Checkbox, COLORS, Size, SystemIcons } from "@laerdal/life-react-components";
import { useState } from "react";
import styled from "styled-components";
import { FacetItemDto, ProductLineFacetItemDto } from "../../../model/dto/articleSearchWithFiltersResponseDto";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterSectionTitle = styled.div`
  color: ${COLORS.neutral_800};
  font-family: Lato;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
`;

interface ArticleSearchFiltersProps {
  articleFilterFacets: FacetItemDto[];
  productLineCodeFacets: ProductLineFacetItemDto[];
  selectedArticleFilters: string[];
  selectedProductLineCodes: string[];
  onToggleArticleFilter: (value: string) => void;
  onToggleProductLineFilter: (code: string) => void;
  onToggleCategory: (categoryName: string, facets: ProductLineFacetItemDto[]) => void;
}

const ArticleSearchFilters = ({
  articleFilterFacets,
  productLineCodeFacets,
  selectedArticleFilters,
  selectedProductLineCodes,
  onToggleArticleFilter,
  onToggleProductLineFilter,
  onToggleCategory,
}: ArticleSearchFiltersProps) => {
  const { t } = useTranslation("articles");
  const { t: tLanding } = useTranslation("landingPage");

  const groupedProductLines = productLineCodeFacets.reduce((acc, facet) => {
    const category = facet.productCategoryName || t('Other');
    if (!acc[category]) acc[category] = [];
    acc[category].push(facet);
    return acc;
  }, {} as Record<string, ProductLineFacetItemDto[]>);

  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const [category, facets] of Object.entries(groupedProductLines)) {
      const hasSelected = facets.some(f => selectedProductLineCodes.includes(f.value));
      initial[category] = !hasSelected;
    }
    return initial;
  });

  return (
    <>
      {articleFilterFacets.length > 0 && (
        <FilterSection>
          <FilterSectionTitle>{t('Topics')}</FilterSectionTitle>
          {articleFilterFacets.map(facet => (
            <Checkbox
              key={facet.value}
              size={Size.Small}
              label={`${facet.label} (${facet.count})`}
              selected={selectedArticleFilters.includes(facet.value)}
              select={() => onToggleArticleFilter(facet.value)}
            />
          ))}
        </FilterSection>
      )}
      {productLineCodeFacets.length > 0 && (
        <FilterSection>
          <FilterSectionTitle>{t('Products')}</FilterSectionTitle>
          {Object.entries(groupedProductLines).map(([category, facets]) => {
            const allSelected = facets.every(f => selectedProductLineCodes.includes(f.value));
            const isCollapsed = collapsedCategories[category] ?? true;
            return (
              <div key={category}>
                <CategoryHeader>
                  <Checkbox
                    size={Size.Small}
                    label={tLanding(category)}
                    selected={allSelected}
                    select={() => onToggleCategory(category, facets)}
                  />
                  <CollapseButton onClick={() => {
                    posthog.capture('SUPP-ArticleSearchPage-CategoryToggled', {
                      categoryName: category,
                      expanded: isCollapsed,
                    });
                    setCollapsedCategories(prev => ({ ...prev, [category]: !isCollapsed }));
                  }}>
                    {isCollapsed ? <SystemIcons.ArrowDropDown /> : <SystemIcons.ArrowDropUp />}
                  </CollapseButton>
                </CategoryHeader>
                {!isCollapsed && (
                  <div style={{ paddingLeft: 24 }}>
                    {facets.map(facet => (
                      <Checkbox
                        key={facet.value}
                        size={Size.Small}
                        label={`${facet.label} (${facet.count})`}
                        selected={selectedProductLineCodes.includes(facet.value)}
                        select={() => onToggleProductLineFilter(facet.value)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </FilterSection>
      )}
    </>
  );
};

export default ArticleSearchFilters;
